<!-- What an action shows below its text: reason, evidence and, when it has one, the ready text (RF-34). -->
<ng-template #details let-item let-closed="closed" let-markable="markable">
  <div class="d-flex flex-column align-items-start gap-1 mt-1">
    <small>{{ item.reason }}</small>
    @for (evidence of item.evidence; track $index) {
      <small class="text-muted d-block text-truncate mw-100">“{{ evidence.quote }}” · {{ when(evidence.at) }}</small>
    }
    @if (item.readyText) {
      <div class="w-100 mt-1">
        <div class="d-flex justify-content-between align-items-center gap-2 mb-1">
          <small class="fw-medium text-muted">{{ "attendance.copilot.actions.readyText" | translate }}</small>
          @if (!closed) {
            <button
              type="button"
              class="btn btn-sm btn-soft-primary font-12"
              [attr.aria-describedby]="textId(item)"
              (click)="useText(item)"
            >
              <i class="ri-add-line me-1"></i>{{ "attendance.copilot.actions.useInReply" | translate }}
            </button>
          }
        </div>
        <p class="bg-primary-subtle text-dark rounded px-2 py-1 mb-0" [id]="textId(item)">{{ item.readyText }}</p>
      </div>
    }
    @if (markable) {
      <button type="button" class="btn btn-link p-0 font-13 text-muted text-decoration-underline" (click)="mark(item, 'dismissed')">
        <small class="fw-medium">{{ "attendance.copilot.actions.dismiss" | translate }}</small>
      </button>
    }
  </div>
</ng-template>

@if (state$ | async; as state) {
  @if (state.view; as view) {
    <div class="copilot-pane__body px-3 pt-2 pb-3 font-13">
      @if (view.actions?.generatedAt; as generatedAt) {
        <small class="text-muted d-block mb-2">
          <i class="ri-time-line me-1"></i
          >{{
            generatedKey(generatedAt)
              | translate
                : {
                    date: (generatedAt | timezoneDate: "DD/MM" : timezone),
                    time: (generatedAt | timezoneDate: "HH:mm" : timezone),
                  }
          }}
        </small>
      }

      <!-- Everyone but the responsible attendant reads the same actions, without the controls that mark (RF-35). -->
      @if (hasActions(view) && !markable(view) && !closed(view)) {
        <small class="text-muted d-block mb-2">
          <i class="ri-lock-line me-1"></i>{{ "attendance.copilot.actions.onlyResponsible" | translate }}
        </small>
      }

      <!-- An insight older than the Actions: they are generated on request, from the current insight (spec M-2). -->
      @if (notGenerated(view)) {
        <p class="text-muted mb-2">{{ "attendance.copilot.actions.notGenerated" | translate }}</p>
        <button type="button" class="btn btn-sm btn-outline-primary" [disabled]="state.requesting" (click)="generate()">
          <i class="ri-refresh-line"></i>
          {{ "attendance.copilot.actions.generate" | translate }}
        </button>
      }

      @if (generating(view)) {
        <copilot-thinking-indicator class="d-block mb-2" [phrases]="[progressKey(view)]" />
      }

      @if (failed(view)) {
        <div class="alert alert-warning py-2 mb-2" role="alert">
          <p class="mb-0"><i class="ri-error-warning-line me-1"></i>{{ failedKey(view) | translate }}</p>
        </div>
        @if (view.canRetry) {
          <button type="button" class="btn btn-sm btn-outline-primary mb-2" [disabled]="state.requesting" (click)="generate()">
            <i class="ri-refresh-line"></i>
            {{ "attendance.copilot.actions.retry" | translate }}
          </button>
        }
      }

      @if (pending(view).length) {
        <ul class="list-unstyled mb-0" [attr.aria-label]="'attendance.copilot.actions.pendingList' | translate">
          @for (item of pending(view); track item.id; let first = $first) {
            <li class="py-2" [ngClass]="{ 'border-top border-light-subtle': !first }">
              @if (markable(view)) {
                <div class="d-flex align-items-start gap-2">
                  <!-- The platform's checkbox; the label wraps with the action's text. It stays enabled while the
                       mark is in flight (the service ignores a second mark), so the box shows checked, not disabled. -->
                  <mat-checkbox class="copilot-actions__check flex-grow-1" [checked]="isMarking(state, item)" (change)="mark(item, 'done')">
                    <span class="fw-medium text-dark">{{ item.action }}</span>
                  </mat-checkbox>
                  <span class="campaign-chip flex-shrink-0" [class.is-brand]="item.owner === 'supervisor'">
                    {{ "attendance.copilot.actions.owner." + item.owner | translate }}
                  </span>
                </div>
                <div class="copilot-actions__details">
                  <ng-container *ngTemplateOutlet="details; context: { $implicit: item, closed: closed(view), markable: true }" />
                </div>
              } @else {
                <div class="d-flex align-items-start gap-2">
                  <i
                    class="ri-checkbox-blank-circle-line font-16 text-muted"
                    role="img"
                    [attr.aria-label]="'attendance.copilot.actions.state.pending' | translate"
                  ></i>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-start gap-2">
                      <p class="fw-medium text-dark flex-grow-1 mb-0">{{ item.action }}</p>
                      <span class="campaign-chip flex-shrink-0" [class.is-brand]="item.owner === 'supervisor'">
                        {{ "attendance.copilot.actions.owner." + item.owner | translate }}
                      </span>
                    </div>
                    <ng-container *ngTemplateOutlet="details; context: { $implicit: item, closed: closed(view), markable: false }" />
                  </div>
                </div>
              }
            </li>
          }
        </ul>
      } @else if (hasActions(view)) {
        <p class="text-muted mb-0">{{ "attendance.copilot.actions.noPending" | translate }}</p>
      }

      <!-- Done and dismissed actions stay listed for the conversation's whole life, collapsed (D-10, D-11). -->
      @if (marked(view).length) {
        <div class="border-top border-light-subtle mt-2 pt-2">
          <button
            type="button"
            class="btn btn-link p-0 font-13 text-muted text-decoration-none"
            [attr.aria-expanded]="markedOpen"
            (click)="toggleMarked()"
          >
            <small class="fw-medium">
              {{ "attendance.copilot.actions.markedGroup" | translate: { count: marked(view).length } }}
              <i [class]="markedOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
            </small>
          </button>
          @if (markedOpen) {
            <ul class="list-unstyled d-flex flex-column gap-2 mt-2 mb-0" [attr.aria-label]="'attendance.copilot.actions.markedList' | translate">
              @for (item of marked(view); track item.id) {
                <li class="d-flex align-items-start justify-content-between gap-2">
                  <div class="flex-grow-1 min-w-0">
                    <p class="text-muted text-decoration-line-through mb-1">{{ item.action }}</p>
                    <div class="d-flex flex-wrap align-items-center gap-2">
                      <span class="badge campaign-badge" [ngClass]="item.state === 'done' ? 'is-ok' : 'is-neutral'">
                        <span class="campaign-badge-dot"></span>{{ "attendance.copilot.actions.state." + item.state | translate }}
                      </span>
                      <small class="text-muted">
                        {{ "attendance.copilot.actions.markedBy" | translate: { name: item.markedBy?.name, time: when(item.markedAt) } }}
                      </small>
                    </div>
                  </div>
                  @if (markable(view)) {
                    <button
                      type="button"
                      class="btn btn-link p-0 font-13 flex-shrink-0 text-decoration-underline"
                      [disabled]="isMarking(state, item)"
                      (click)="mark(item, 'pending')"
                    >
                      <small class="fw-medium">{{ "attendance.copilot.actions.reopen" | translate }}</small>
                    </button>
                  }
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  }
}
